/**
 * Vertex group surface visualization — tints faces by vertex group membership
 * while in edit mode. Optional, gated by `editMode.showVertexGroupColors`.
 *
 * Membership semantics (matches `resolveFaceMaterialIndices`):
 *  - A face belongs to a group iff every one of its corners is in the group's
 *    index set.
 *  - Where multiple groups claim the same face, the later group in
 *    `obj.vertexGroups` wins (overlay precedence).
 *  - Faces in no group stay untinted; the base material shows through.
 *
 * Rendering: one `<mesh>` per non-empty group bucket, sharing a single
 * BufferGeometry.position attribute backed by the live-preview vertex array.
 * Each bucket has its own `createOverlayFaceMaterial` instance with the
 * group's color. Selection highlight (orange) paints OVER these tints because
 * SelectionHighlight is mounted after this overlay in EditModeRenderer.
 */

import { useEffect, useMemo } from "react";
import * as THREE from "three/webgpu";
import { useResolvedLiveEditGeometry } from "../app/useLiveEditPreview.ts";
import type { PolygonTopology, VertexGroup } from "../modeling/types.ts";
import { resolveFaceVertexGroupIndices } from "../modeling/vertexGroups.ts";
import { updateSharedVertexPositions } from "./EditModeRenderer.tsx";
import { createOverlayFaceMaterial } from "./editDots.ts";
import { groupColorAt } from "./groupColors.ts";
import { useDispose, useDisposeMany } from "./hooks/useDispose.ts";

interface FaceVertexGroupBucket {
  groupIdx: number;
  indices: Uint32Array;
}

function bucketFacesByGroup(
  topology: PolygonTopology,
  faceGroupIndices: Int32Array,
  hiddenFaces: Set<number>,
): FaceVertexGroupBucket[] {
  const { offsets, corners } = topology;
  const groupCount = faceGroupIndices.reduce(
    (max, gi) => (gi > max ? gi : max),
    -1,
  );
  if (groupCount < 0) return [];

  const triCounts = new Uint32Array(groupCount + 1);
  for (let f = 0; f < faceGroupIndices.length; f++) {
    const gi = faceGroupIndices[f];
    if (gi < 0) continue;
    if (hiddenFaces.has(f)) continue;
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCounts[gi] += size - 2;
  }

  const buckets: FaceVertexGroupBucket[] = [];
  const writers: Uint32Array[] = [];
  const writePositions = new Uint32Array(groupCount + 1);
  for (let gi = 0; gi <= groupCount; gi++) {
    if (triCounts[gi] === 0) continue;
    const buf = new Uint32Array(triCounts[gi] * 3);
    writers[gi] = buf;
    buckets.push({ groupIdx: gi, indices: buf });
  }

  for (let f = 0; f < faceGroupIndices.length; f++) {
    const gi = faceGroupIndices[f];
    if (gi < 0) continue;
    if (hiddenFaces.has(f)) continue;
    const start = offsets[f];
    const end = offsets[f + 1];
    const size = end - start;
    if (size < 3) continue;
    const buf = writers[gi];
    if (!buf) continue;
    let w = writePositions[gi];
    const v0 = corners[start];
    for (let i = 1; i < size - 1; i++) {
      buf[w++] = v0;
      buf[w++] = corners[start + i];
      buf[w++] = corners[start + i + 1];
    }
    writePositions[gi] = w;
  }

  return buckets;
}

interface VertexGroupColorOverlayProps {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  vertexGroups: ReadonlyArray<VertexGroup>;
  hiddenFaces: Set<number>;
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}

export function VertexGroupColorOverlay({
  objectId,
  vertices,
  topology,
  vertexGroups,
  hiddenFaces,
  dirtyVertices,
  xray,
}: VertexGroupColorOverlayProps) {
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;

  // Per-face → group index. Recomputes when topology or vertex group
  // membership changes; the SceneObject reference changes on assign/remove.
  const faceGroupIndices = useMemo(
    () => resolveFaceVertexGroupIndices(topology, vertexGroups),
    [topology, vertexGroups],
  );

  // Bucket faces by group → triangulated index buffers.
  const buckets = useMemo(() => {
    if (!faceGroupIndices) return [];
    return bucketFacesByGroup(topology, faceGroupIndices, hiddenFaces);
  }, [topology, faceGroupIndices, hiddenFaces]);

  // Shared position attribute reused across every bucket's geometry.
  // Intentionally depend on `previewVertices.length` — not the array reference
  // itself — so the GPU buffer survives every drag frame (the live-preview
  // hook hands back a new typed array each frame). Live position updates flow
  // through `updateSharedVertexPositions` in the effect below.
  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on length only — array reference changes every drag frame, but the GPU buffer must persist
  const positionAttr = useMemo(
    () => new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    [previewVertices.length],
  );

  // One BufferGeometry per bucket, all sharing the same position attribute.
  const bucketGeos = useMemo(() => {
    const geos: THREE.BufferGeometry[] = [];
    for (const b of buckets) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", positionAttr);
      geo.setIndex(new THREE.BufferAttribute(b.indices, 1));
      geos.push(geo);
    }
    return geos;
  }, [buckets, positionAttr]);
  useDisposeMany(...bucketGeos);

  // One translucent face material per bucket, colored by group index.
  const bucketMats = useMemo(
    () =>
      buckets.map((b) =>
        createOverlayFaceMaterial({
          color: `#${groupColorAt(b.groupIdx).getHexString()}`,
          opacity: 0.55,
          depthTest: !xray,
          side: xray ? THREE.DoubleSide : THREE.FrontSide,
        }),
      ),
    [buckets, xray],
  );
  useDisposeMany(...bucketMats);

  // Update positions in-place during drag. All buckets share `positionAttr`
  // so a single update covers every visible mesh.
  useEffect(() => {
    if (positionAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        positionAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    }
  }, [
    positionAttr,
    previewVertices,
    previewDirtyVertices,
    previewDirtyPositions,
  ]);

  // Hold a reference to a no-op disposable so the position attribute is
  // released when this overlay unmounts (geometries don't dispose attributes
  // they don't own).
  useDispose({
    dispose: () => {
      // Nothing to free explicitly — the BufferAttribute itself has no GPU
      // resources beyond what the geometries already own. Kept for symmetry
      // with the other overlay components.
    },
  });

  if (bucketGeos.length === 0) return null;

  return (
    <>
      {bucketGeos.map((geo, i) => (
        <mesh
          // biome-ignore lint/suspicious/noArrayIndexKey: buckets are stable per memo
          key={i}
          geometry={geo}
          material={bucketMats[i]}
          renderOrder={0}
        />
      ))}
    </>
  );
}
